// A product's Sessions page (design 5.3's "the product's upcoming sessions"; no board draws it, so
// its words are on the list for Adam): their own intake's live sessions, soonest first, then the ones
// that have run. Join opens from join_opens_minutes before a session until it ends (the Library's
// rule and link), Add to calendar saves 1 session with the hub's page in it, never the Zoom link,
// and a session that has run links to its week once the recording is up, or, where sessions have no
// weeks (a mastermind's group sessions), to the recording made at it (recordings.session_id).
// Members with no intake have no Sessions in their menu, so they never reach this page from it. A
// mastermind's are their group's and every group's; the eyebrow is their group, with its schedule
// ("Group 2, monthly, Sundays"). Once any of the product's recordings has Adam's own thumbnail
// (decision 47, 12 Oct), each session that has run starts with its recording's 16:9 frame in place of
// the date tile: the thumbnail, or the grey panel with the session's date where there's none or no
// recording yet (product/recordingThumbs.js), and where it shows the picture, the session's date goes in
// front of its times instead ("Sun 27 Sep, 5pm to 8pm UK time"). Sessions still to come keep their date
// tile. A mastermind's button says Join on Zoom, as its home does (the Accelerator critique's member 14,
// 20 Oct); every other type's says Join. On a mastermind, a session the member missed live whose recording
// they haven't watched, from their own accountability log (the Accelerator's rows: useMastermind), says "You
// missed this one" in amber, with Watch the recording as its main button (member 9); every other type is as it was.
import { useState } from 'react'
import * as f from '../lib/format.js'
import { Link } from '../lib/router.jsx'
import { useNow } from '../lib/clock.js'
import { downloadIcs } from '../lib/calendar.js'
import { useMember } from '../app/member.jsx'
import { useProductData } from './ProductData.jsx'
import { isReleased, moduleRecordings, myIntake, nameWithSchedule, progressFor, recordingForModule } from './model.js'
import { anyThumbnail, cardThumb } from './recordingThumbs.js'
import { RecordingThumb, useRecordingThumbs } from './RecordingThumb.jsx'
import { useMastermind } from '../products/accelerator/useMastermind.js'
import { missedUnwatched } from '../products/accelerator/model.js'
import { MISSED_MARK } from './tagged.js'

const MIN = 60e3
const zoomLink = url => (typeof url === 'string' && /^https:\/\//.test(url) ? url : null)

// missed: a mastermind's sessions missed live with the recording not watched (accelerator/model.js missedUnwatched),
// or none
export function sessionRows(data, product, nowMs, missed = null) {
  const join = (product.join_opens_minutes ?? 15) * MIN
  const any = anyThumbnail(data.recordings)
  return (data.sessions || []).filter(s => s.starts_at && s.status !== 'cancelled').map(s => {
    const start = Date.parse(s.starts_at)
    const end = start + s.duration_minutes * MIN
    const mod = s.module_id ? (data.modules || []).find(m => m.id === s.module_id) : null
    const title = mod && !f.isPlaceholder(mod.title) && !s.title.includes(mod.title) ? `${s.title}: ${mod.title}` : s.title
    const live = start <= nowMs && nowMs < end
    const past = end <= nowMs
    // the recording made at it, once released: its week's, or its own
    const own = !mod ? (data.recordings || []).find(r => r.session_id === s.id && r.key && isReleased(data, r.id)) || null : null
    const open = mod ? moduleRecordings(data, mod.id).some(r => isReleased(data, r.id)) : Boolean(own)
    // the recording its frame shows, once it has run: its week's (the 1 its page plays first), or the
    // 1 made at it; the frame's date is the session's own, and its picture waits until that recording
    // is open to them (Adam, 12 Oct)
    const rec = mod ? recordingForModule(data, mod.id) : (data.recordings || []).find(r => r.session_id === s.id) || null
    let status
    if (live) status = 'Live now'
    else if (past) status = open ? null : 'The recording goes up after the session'
    else if (f.daysFrom(nowMs, start) === 0) status = nowMs >= start - join ? 'Join is open' : `Join opens at ${f.time(start - join)}`
    else status = null
    return {
      id: s.id, block: f.dateBlock(start), title, start, end, past, live,
      time: `${f.time(start)} to ${f.time(end)} UK time`,
      status,
      join: !past && nowMs >= start - join ? zoomLink(s.zoom_join_url) : null,
      week: past && open ? (mod ? `${product.path}/${mod.key}` : `${product.path}/${own.key}`) : null,
      calendar: past ? null : { uid: s.id, title: `${product.name}, ${s.title}`, startMs: start, endMs: end, path: product.path || '/' },
      thumb: past ? cardThumb(rec, { any, block: f.dateBlock(start), open: Boolean(rec) && isReleased(data, rec.id) }) : null,
      // missed live, and its recording not watched since (their progress)
      missed: Boolean(past && missed?.has(s.id) && !(rec && progressFor(data, rec.id)?.watched_at)),
    }
  }).sort((a, b) => (a.past === b.past ? (a.past ? b.start - a.start : a.start - b.start) : a.past ? 1 : -1))
}

export function SessionsPage({ product }) {
  const { data } = useProductData()
  const { me } = useMember()
  const nowMs = useNow()
  const p = data.product || product
  // a mastermind's own log, for the sessions they missed (its home's rows, shared); nothing for any other type
  const mm = useMastermind(p.id, me.person.id, { enabled: p.type === 'mastermind' })
  const rows = sessionRows(data, p, nowMs, p.type === 'mastermind' ? missedUnwatched(mm.data?.log) : null)
  const intake = myIntake(data)
  const sub = intake ? nameWithSchedule(intake.name, intake.schedule_label) : null
  const coming = rows.filter(r => !r.past)
  const been = rows.filter(r => r.past)
  return (
    <div className="pl">
      <section className="pl-head">
        {sub && <div className="pl-eyebrow">{sub}</div>}
        <h1 className="pl-h1">Sessions</h1>
      </section>
      {!rows.length && <p className="pp-empty">No live sessions for you here.</p>}
      {coming.length > 0 && <SessionList heading="Coming up" rows={coming} join={joinLabel(p)} />}
      {been.length > 0 && <SessionList heading="Already run" rows={been} join={joinLabel(p)} />}
    </div>
  )
}

// The Join button's words: a mastermind's Join on Zoom, every other type's Join
export const joinLabel = product => (product?.type === 'mastermind' ? 'Join on Zoom' : 'Join')

function SessionList({ heading, rows, join }) {
  const thumbs = useRecordingThumbs(rows.map(r => r.thumb))
  return (
    <section className="pl-group">
      <h2 className="pl-h2">{heading}</h2>
      <div className="pl-list">
        {rows.map(r => <SessionRow key={r.id} row={r} thumbs={thumbs} join={join} />)}
      </div>
    </section>
  )
}

function SessionRow({ row, thumbs, join = 'Join' }) {
  const [added, setAdded] = useState(false)
  return (
    <div className={[row.thumb ? 'pl-row pl-row--thumb' : 'pl-row', row.missed ? 'pl-row--missed' : ''].filter(Boolean).join(' ')}>
      {row.thumb
        ? <RecordingThumb card={row.thumb} thumbs={thumbs} className="pl-thumb" />
        : <span className="pl-date" aria-hidden="true"><span>{row.block.dow}</span><strong>{row.block.day}</strong><span>{row.block.mon}</span></span>}
      <span className="pl-text">
        <span className="pl-title">{row.title}</span>
        {row.missed && <span className="pl-missed">{MISSED_MARK}</span>}
        <span className="pl-meta">{row.thumb?.item ? `${f.shortDate(row.start)}, ` : ''}{row.time}{row.status ? `. ${row.status}` : ''}</span>
      </span>
      <span className="pl-actions">
        {row.join && <a className="pl-btn pl-btn--main" href={row.join} target="_blank" rel="noopener noreferrer" aria-label={`${join}: ${row.title}`}>{join}</a>}
        {row.calendar && (
          <button type="button" className="pl-btn" onClick={() => { downloadIcs(row.calendar); setAdded(true) }}>
            {added ? 'Added to your calendar' : 'Add to calendar'}
          </button>
        )}
        {row.week && <Link className={row.missed ? 'pl-btn pl-btn--main' : 'pl-btn'} to={row.week}>Watch the recording</Link>}
      </span>
    </div>
  )
}
